/**
 * This file contains shared types/classes/interfaces in the analyze-image module. Note that these types represent our API contract, changes must
 * be made with care and in a non-breaking way.
 */

import type { ValueOf } from 'type-fest';

import type { TUserValidation } from './latestImages';

export enum ImageAnalysisState {
  PROCESSING = 'PROCESSING',
  COMPLETE = 'COMPLETE',
  ERROR = 'ERROR',
}

export type TImagetype = ValueOf<typeof ImageType>;

export const ImageType = {
  BITEWING: 'BITEWING',
  PERIAPICAL: 'PERIAPICAL',
  PANORAMIC: 'PANORAMIC',
  OTHER: 'OTHER',
  COLOR_IMAGE: 'COLOR_IMAGE',
  INTRAORAL: 'INTRAORAL',
  CONE_BEAM_CT: 'CONE_BEAM_CT',
} as const;

export enum AnalyzeImageVideaErrorCode {
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  INVALID_IMAGE_TYPE = 'INVALID_IMAGE_TYPE',
  IMAGE_NOT_FOUND = 'IMAGE_NOT_FOUND',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  ANALYSIS_FAILURE = 'ANALYSIS_FAILURE',
  IMAGE_SHARPENING_ERROR = 'IMAGE_SHARPENING_ERROR',
  GENERIC_ANALYSIS_ERROR = 'GENERIC_ANALYSIS_ERROR',
}

export enum CariesType {
  VIDEA_DENTAL_ASSIST_CARIES = 'VIDEA_DENTAL_ASSIST_CARIES',
  CARIES_BW = 'CARIES_BW',
  CARIES_PA = 'CARIES_PA',
}

// mapped response from connector
export enum ModelState {
  SUCCESS = 'SUCCESS',
  INVALID_IMAGE_TYPE = 'INVALID_IMAGE_TYPE',
}

export enum RBLType {
  cej = 'cej',
  abd = 'abd',
  root = 'root',
  abl = 'abl',
}

export enum RBLPointSide {
  left = 'left',
  right = 'right',
  center = 'center',
}

export enum RBLSide {
  mesial = 'mesial',
  distal = 'distal',
}

export enum AppType {
  VIDEA_ASSIST = 'Videa AI',
  VIDEA_INSIGHT = 'Videa Insights',
}

export enum UserRole {
  HYGIENIST = 'HYGIENIST',
  DENTIST = 'DENTIST',
  PRACTICE_MANAGER = 'PRACTICE_MANAGER',
  PRACTICE = 'PRACTICE',
  OTHER = 'OTHER',
  DENTAL_DIRECTOR = 'DENTAL_DIRECTOR',
}

export type BoundingBox = {
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
  x?: number;
  y?: number;
};

export interface BoundingBoxFindingDto extends BoundingBox {
  id: string;
}

export interface ToothIdentificationFindingDto extends BoundingBoxFindingDto {
  toothNumber: number | string;
}

export type RBLPoint = {
  id: string;
  x: number;
  y: number;
  rblType: RBLType;
};

export type RBLSideLine = {
  side: RBLSide;
  distanceMM: number | null;
  points: RBLPoint[];
  userValidation?: TUserValidation;
};

export type RBLLineFindingDto = {
  toothNumber: number | string;
  sides: RBLSideLine[];
};

export type ModelResultsDto = {
  modelState: ModelState;
  modelId: string;
};

export type BoundingBoxResultsDto = ModelResultsDto & {
  findings: BoundingBoxFindingDto[];
};

export type ToothIdentificationResultsDto = ModelResultsDto & {
  findings: ToothIdentificationFindingDto[];
};

export type RadiographicBoneLossResultsDto = ModelResultsDto & {
  findings: RBLLineFindingDto[];
};

export type CariesResultsDto = BoundingBoxResultsDto & {
  vcaMessage: string;
  type?: CariesType;
};

export interface ToothThirdsFindingResultsDto {
  toothIdentifier: string;
  toothStructureDecay?: ToothStructureDecay;
  matchedCaries?: MatchedCaries[];
}
export interface ToothStructureDecay {
  decay_crown: number;
  cervical_mesial_third: number[];
  decay_subcrestal: number;
  decay_tooth: number;
  num_lesions: number;
}

export interface MatchedCaries extends BoundingBox {
  confidenceScore: number;
  vertical_thirds: string[];
  horizontal_thirds: string[];
  lesionDescriptor: string;
}

export type ToothThirdsResultsDto = ModelResultsDto & {
  findings: ToothThirdsFindingResultsDto[];
};

export enum CariesCategory {
  PRIMARY_CARIES = 'PRIMARY_CARIES',
  SECONDARY_CARIES_CROWN = 'SECONDARY_CARIES_CROWN',
  SECONDARY_CARIES_NON_CROWN = 'SECONDARY_CARIES_NON_CROWN',
  SHORT_OR_OVERHANGING_RESTORATION_MARGIN = 'SHORT_OR_OVERHANGING_RESTORATION_MARGIN',
  OPEN_RESTORATION_MARGIN = 'OPEN_RESTORATION_MARGIN',
  CLOSED_RESTORATION_MARGIN = 'CLOSED_RESTORATION_MARGIN',
  VOID_LINER_BOND_LOW_DENSITY_RESTORATION = 'VOID_LINER_BOND_LOW_DENSITY_RESTORATION',
  LOST_CROWN = 'LOST_CROWN',
  BROKEN_CUSP = 'BROKEN_CUSP',
  OTHER_NON_CARIES = 'OTHER_NON_CARIES',
}

export enum CariesSeverity {
  NA = 'NA',
  INCIPIENT = 'INCIPIENT',
  INITIAL = 'INITIAL',
  MODERATE = 'MODERATE',
  ADVANCED = 'ADVANCED',
  SEVERE = 'SEVERE',
}

export enum CariesLocation {
  INTERPROXIMAL = 'INTERPROXIMAL',
  OCCLUSAL = 'OCCLUSAL',
  BUCCAL_LINGUAL = 'BUCCAL_LINGUAL',
  ROOT = 'ROOT',
  RESTORATION_ADJACENT = 'RESTORATION_ADJACENT',
  OTHER_OR_NA = 'OTHER_OR_NA',
}
export interface CariesSubClassFindingResultsDto
  extends BoundingBoxFindingWithConfidenceDto {
  category: CariesCategory;
  severity: CariesSeverity;
  location: CariesLocation;
  categoryScore: number;
  severityScore: number;
  locationScore: number;
}
export interface BoundingBoxFindingWithConfidenceDto extends BoundingBox {
  confidenceScore: number;
  BoxId?: string;
}

export type CariesSubClassificationDto = ModelResultsDto & {
  findings: CariesSubClassFindingResultsDto[];
};

export type RestorativeAdjacentLucencyResultsDto = ModelResultsDto & {
  findings: BoundingBoxFindingDto[];
};

export type AnalyzeImageFindingsDto = {
  caries: CariesResultsDto | null;
  periapical_radiolucency: BoundingBoxResultsDto | null;
  radiographic_boneloss: RadiographicBoneLossResultsDto | null;
  tooth_identification: ToothIdentificationResultsDto | null;
  tooth_thirds: ToothThirdsResultsDto | null;
  calculus: BoundingBoxResultsDto | null;
  caries_subclassification: CariesSubClassificationDto | null;
  restorative_adjacent_lucency: RestorativeAdjacentLucencyResultsDto | null;
};

/**
 * These keys are intended to be used with the images/get-images endpoint
 */
export class ImageKeys {
  /** Full s3 path for debugging purposes only. Clients should not use this field.   */
  // @ts-expect-error TS(2564): Property 's3path' has no initializer and is not de... Remove this comment to see the full error message
  s3path: string;

  /**
   * Key for originally uploaded file. Example: 'file_001.dcm'
   */
  // @ts-expect-error TS(2564): Property 'original' has no initializer and is not ... Remove this comment to see the full error message
  original: {
    key: string | null;
  };
  /**
   * Key for sharpened image. Example: 'for_presentation/file_001_v3.1.2.dcm'
   * May be null due to error in image processing. If so, use original image.
   */

  // @ts-expect-error TS(2564): Property 'sharpened' has no initializer and is not... Remove this comment to see the full error message
  sharpened: {
    key: string | null;
  };
  /**
   * Key for sharpened image. Example: 'for_presentation/file_001_v6.0.0.dcm'
   * May be null due to error in image processing. If so, use sharpened image.
   */

  // @ts-expect-error TS(2564): Property 'generalized' has no initializer and is n... Remove this comment to see the full error message
  generalized: {
    key: string | null;
  };
}

export type UserModifications = {
  brightnessValue?: number;
  contrastValue?: number;
  hqEnabled?: boolean;
  imageMirrored?: boolean;
  opacityInverted?: boolean;
};

export type AnalyzeImageVideaError = {
  analysesFailed: FailedAnalysisType[];
  analysisErrorMessagingCode: AnalysisErrorMessagingCode[];
};

export enum AnalysisErrorMessagingCode {
  SUCCESS = 'SUCCESS',
  VCA_FAILED = 'VCA_FAILED',
  RBL_FAILED = 'RBL_FAILED',
  PA_FAILED = 'PA_FAILED',
  PRL_FAILED = 'PRL_FAILED',
  CALCULUS_FAILED = 'CALCULUS_FAILED',
  TDIS_FAILED = 'TDIS_FAILED',
  CARIES_ONLY = 'CARIES_ONLY',
  TWO_FAILURES = 'TWO_FAILURES',
  MORE_THAN_TWO_FAILURES = 'MORE_THAN_TWO_FAILURES',
  FAILED = 'FAILED',
}

export enum FailedAnalysisType {
  CARIES_BW = 'CARIES_BW',
  PERIAPICAL_RADIOLUCENCY = 'PERIAPICAL_RADIOLUCENCY',
  RADIOGRAPHIC_BONE_LOSS = 'RADIOGRAPHIC_BONE_LOSS',
  TOOTH_IDENTIFICATION = 'TOOTH_IDENTIFICATION',
  TOOTH_THIRDS = 'TOOTH_THIRDS',
  CALCULUS = 'CALCULUS',
  CARIES_SUBCLASSIFICATION = 'CARIES_SUBCLASSIFICATION',
  VIDEA_DENTAL_ASSIST = 'VIDEA_DENTAL_ASSIST',
}

export type LatestPatients = {
  id: string;
  firstName: string;
  lastName: string;
  latestVisitAnalyzedImagesCount: number;
  name?: string;
  estimatedVisitDate?: string;
  viewed: boolean;
};

export enum DataSource {
  NUCLEUS = 'NUCLEUS',
  CONNECTOR = 'CONNECTOR',
}

export enum NavMenu {
  DASHBOARD = 'This Month’s Metrics',
  LOCATIONS = 'Locations',
}

export type PerformanceRevenue = {
  amount: number;
  type: string;
};

export type PerformanceTotalRevenue = {
  amount: number;
};

export type Provider = {
  practiceId: string;
  providerId: string;
  providerName: string;
  treatmentsProposed: number;
  treatmentsScheduled: number;
  treatmentsCompleted: number;
  totalRevenue: number;
  acceptanceRate: number;
  currencyCode: string;
};

export type LocationDetail = {
  name: string;
  location: string;
  isWatchListed: boolean;
  id: string;
};

export type User = {
  practice?: any;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  organizations?: Array<any>;
  id: string;
  primaryOrganizationId: string;
  primaryPracticeId: string;
  primaryPracticeName?: string;
  primaryOrganizationName?: string;
  salesDemoPracticeConfig?: {
    practiceId: string;
    practiceName: string;
  };
  userRole: string;
};

export type Meta = {
  page: number;
  pageSize: number;
  itemsCount: number;
  totalPages: number;
};

// Can get more symbols from
// https://www.eurochange.co.uk/travel-money/world-currency-abbreviations-symbols-and-codes-travel-money
export const CURRENCY_SYMBOLS = {
  USD: '$', // US Dollar
  EUR: '€', // Euro
  CRC: '₡', // Costa Rican Colón
  GBP: '£', // British Pound Sterling
  ILS: '₪', // Israeli New Sheqel
  INR: '₹', // Indian Rupee
  JPY: '¥', // Japanese Yen
  KRW: '₩', // South Korean Won
  NGN: '₦', // Nigerian Naira
  PHP: '₱', // Philippine Peso
  PLN: 'zł', // Polish Zloty
  PYG: '₲', // Paraguayan Guarani
  THB: '฿', // Thai Baht
  UAH: '₴', // Ukrainian Hryvnia
  VND: '₫', // Vietnamese Dong
};

export const DEFAULT_CURRENCY_CODE = 'USD';

export enum OpDashboardApiEndpoints {
  REPORT_SUMMARY = '/reports/summary',
  LOCATIONS_BY_REVENUE = '/reports/practices/summary',
  TREATMENT_STATS = '/reports/treatments/stats',
  WATCHLIST_SUMMARY = '/watchlist/summary',
  ADD_REMOVE_WATCH_LIST = '/watchlist/user',
  PROVIDERS = '/reports/practices/%s/providers',
}

export type InsightsDashboardMetric = {
  name: string;
  currency?: string;
  value: string | number;
  Icon?: any;
  iconPath?: any;
  unit?: string | null;
  message?: string;
  className?: string;
};

export enum SortOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export const TOP_LOCATIONS = 'TOP_LOCATIONS';
export const BOTTOM_LOCATIONS = 'BOTTOM_LOCATIONS';

export enum ChartType {
  BAR = 'bar',
  LINE = 'line',
}

export enum LocationFilterType {
  ALL_LOCATIONS = 'All Locations',
  WATCHLIST_ONLY = 'Watchlist Only',
}

export const ChartProductionRevenueType = {
  AI_FINDINGS: {
    label: 'AI Findings',
    borderColor: 'rgb(255, 99, 132)',
    backgroundColor: '#03267A',
    yAxisID: 'y',
  },
  RECOMMENDED_PROCEDURES: {
    label: 'Treatments Presented',
    borderColor: 'rgb(255, 99, 132)',
    backgroundColor: '#353AE8',
    yAxisID: 'y',
  },
  TREATMENTS_ACCEPTED: {
    label: 'Treatments Scheduled',
    borderColor: 'rgb(255, 99, 132)',
    backgroundColor: '#7736EA',
    yAxisID: 'y',
  },
  TREATMENTS_PERFORMED: {
    label: 'Treatments Completed',
    borderColor: 'rgb(255, 99, 132)',
    backgroundColor: '#CC28E8',
    yAxisID: 'y',
  },
  REVENUE: {
    label: 'Revenue',
    borderColor: '#329F2D',
    backgroundColor: '#329F2D',
    yAxisID: 'y1',
    lineTension: 0.4,
    pointRadius: 0,
    pointHoverRadius: 0,
  },
};

export enum CornerstoneAnnotationFindingTypes {
  caries = 'caries',
  periapicalRadiolucency = 'periapicalRadiolucency',
  radiographicBoneloss = 'radiographicBoneloss',
  calculus = 'calculus',
}

export enum PmsType {
  OPEN_DENTAL = 'OPEN_DENTAL',
  DENTRIX = 'DENTRIX',
  DENTRIX_ASCEND = 'DENTRIX_ASCEND',
}

export enum Modules {
  Auth = 'auth',
  Viewer = 'viewer',
  Huddle = 'huddle',
  Claims = 'claims',
  Ambient = 'ambient',
  Insights = 'insights',
  LandingPage = 'landing page',
  Switchboard = 'switchboard',
  External = 'external',
  Magic = 'magic',
  InsuranceExport = 'insurance export',
  Trial = 'trial',
  default = Auth,
}

export enum Layouts {
  Page = 'page',
  Modal = 'modal',
}
